import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { DeviceChannel } from '../deviceModel';
import { frameRawValue, opGet, opSetRaw } from './ops';
import {
  DETECTION_TYPES,
  canonicalizeWeeklySchedule,
  copyDayRules,
  emptyWeeklySchedule,
  minuteToTime,
  parseWeeklySchedule,
  replaceDayRules,
  rulesForDay,
  timeToMinute,
  type DetectionType,
  type RecordingRuleMode,
  type ScheduleKind,
  type WeeklyRule,
  type WeeklySchedule,
} from './schedules';

type LoadState = 'idle' | 'loading' | 'ready' | 'unavailable';

interface Props {
  deviceId: string;
  attrId: string;
  kind: ScheduleKind;
  wsOpen: boolean;
  disabled: boolean;
  channels?: DeviceChannel[];
}

const DAY_KEYS = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'] as const;

function toWire(schedule: WeeklySchedule): Record<string, unknown> {
  return schedule as unknown as Record<string, unknown>;
}

export function WeeklyScheduleEditor({ deviceId, attrId, kind, wsOpen, disabled, channels = [] }: Props) {
  const { t } = useTranslation();
  const usableChannels = useMemo(() => channels.filter((channel) => channel.enabled), [channels]);
  const [activeChannel, setActiveChannel] = useState<number | undefined>(() => usableChannels[0]?.ch);
  const [copyChannels, setCopyChannels] = useState<Set<number>>(() => new Set());
  const [state, setState] = useState<LoadState>('idle');
  const [draft, setDraft] = useState<WeeklySchedule>(() => emptyWeeklySchedule());
  const [selectedDay, setSelectedDay] = useState(1);
  const [copyDays, setCopyDays] = useState<Set<number>>(() => new Set());
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const isChannelized = kind === 'recording' && usableChannels.length > 0;
  const dayRules = rulesForDay(draft, selectedDay);

  useEffect(() => {
    if (!isChannelized) setActiveChannel(undefined);
    else if (!usableChannels.some((channel) => channel.ch === activeChannel)) setActiveChannel(usableChannels[0]?.ch);
  }, [activeChannel, isChannelized, usableChannels]);

  useEffect(() => {
    if (!wsOpen || (isChannelized && activeChannel === undefined)) {
      setState('idle');
      return;
    }
    let alive = true;
    setState('loading');
    setError(null);
    setNotice(null);
    opGet(deviceId, attrId, activeChannel).then(
      (frame) => {
        if (!alive) return;
        const parsed = parseWeeklySchedule(frameRawValue(frame), kind);
        if (!parsed) {
          setState('unavailable');
          return;
        }
        setDraft(parsed);
        setCopyChannels(new Set());
        setState('ready');
      },
      () => { if (alive) setState('unavailable'); }
    );
    return () => { alive = false; };
  }, [activeChannel, attrId, deviceId, isChannelized, kind, wsOpen]);

  if (state === 'idle' || state === 'unavailable') return null;
  if (state === 'loading') {
    return <p className="text-secondary" role="status" style={{ margin: 0 }}>{t('deviceSettings.scheduleLoading')}</p>;
  }

  const updateDay = (rules: WeeklyRule[]) => {
    try {
      setDraft(replaceDayRules(draft, selectedDay, rules, kind));
      setError(null);
    } catch {
      setError(t('deviceSettings.scheduleOverlapError'));
    }
  };

  const updateRule = (index: number, patch: Partial<WeeklyRule>) => {
    const next = dayRules.map((rule, i) => (i === index ? { ...rule, ...patch } : rule));
    updateDay(next);
  };

  const addRule = () => {
    const lastEnd = dayRules.length > 0 ? dayRules[dayRules.length - 1].end_min : 480;
    const start = Math.min(lastEnd, 1380);
    const end = Math.min(1440, Math.max(start + 60, start === 1380 ? 1440 : 1080));
    const rule: WeeklyRule = kind === 'recording'
      ? { days_mask: 1 << selectedDay, start_min: start, end_min: end, mode: 'event' }
      : { days_mask: 1 << selectedDay, start_min: start, end_min: end };
    updateDay([...dayRules, rule]);
  };

  const applyCopyDays = () => {
    try {
      setDraft(copyDayRules(draft, selectedDay, [...copyDays], kind));
      setCopyDays(new Set());
      setError(null);
      setNotice(t('deviceSettings.scheduleCopiedDays'));
    } catch {
      setError(t('deviceSettings.scheduleOverlapError'));
    }
  };

  const save = async () => {
    setSaving(true);
    setError(null);
    setNotice(null);
    try {
      const canonical = canonicalizeWeeklySchedule(draft, kind);
      const targets = isChannelized
        ? [...new Set([activeChannel, ...copyChannels].filter((value): value is number => value !== undefined))]
        : [undefined];
      const results = await Promise.allSettled(targets.map((channel) => opSetRaw(deviceId, attrId, toWire(canonical), channel)));
      const failed = results.filter((result) => result.status === 'rejected').length;
      if (failed > 0) setError(t('deviceSettings.schedulePartialFailure', { failed, total: targets.length }));
      else {
        setDraft(canonical);
        setCopyChannels(new Set());
        setNotice(t('deviceSettings.scheduleSaved', { count: targets.length }));
      }
    } catch {
      setError(t('deviceSettings.scheduleInvalid'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid={`${kind}-weekly-schedule`} style={{ borderTop: '1px solid var(--color-border)', marginTop: 'var(--spacing-3)', paddingTop: 'var(--spacing-4)', display: 'grid', gap: 'var(--spacing-4)' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 'var(--spacing-4)', flexWrap: 'wrap' }}>
        <strong>{kind === 'detection' ? t('deviceSettings.detectionSchedule') : t('deviceSettings.recordSchedule')}</strong>
        {isChannelized && (
          <label>
            {t('deviceSettings.scheduleChannel')}{' '}
            <select value={activeChannel ?? ''} disabled={saving} onChange={(event) => setActiveChannel(Number(event.target.value))}>
              {usableChannels.map((channel) => <option key={channel.ch} value={channel.ch}>{channel.name?.trim() || t('deviceSettings.channelNumber', { channel: channel.ch })}</option>)}
            </select>
          </label>
        )}
      </div>
      <label style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-2)' }}>
        <input type="checkbox" checked={draft.enabled} disabled={disabled || saving} onChange={(event) => setDraft({ ...draft, enabled: event.target.checked })} />
        <span>{t('deviceSettings.scheduleEnabled')}</span>
      </label>
      <span className="text-secondary" style={{ fontSize: 'var(--font-size-sm)' }}>{t('deviceSettings.scheduleTimezone', { timezone: draft.timezone })}</span>

      <div style={{ display: 'flex', gap: 'var(--spacing-2)', flexWrap: 'wrap' }}>
        {DAY_KEYS.map((key, day) => (
          <button key={key} type="button" className={selectedDay === day ? 'btn-primary' : 'btn-ghost'} onClick={() => setSelectedDay(day)}>
            {t(`deviceSettings.scheduleDay${key[0].toUpperCase()}${key.slice(1)}`)}
          </button>
        ))}
      </div>

      {dayRules.length === 0 ? <span className="text-secondary">{t('deviceSettings.scheduleNoRules')}</span> : dayRules.map((rule, index) => (
        <div key={`${rule.start_min}-${rule.end_min}-${index}`} style={{ display: 'grid', gridTemplateColumns: 'auto auto 1fr auto', gap: 'var(--spacing-3)', alignItems: 'center' }}>
          <input type="time" value={minuteToTime(rule.start_min)} disabled={disabled || saving} onChange={(event) => {
            const value = timeToMinute(event.target.value);
            if (value != null) updateRule(index, { start_min: value });
          }} aria-label={t('deviceSettings.scheduleStart')} />
          <input type="time" value={minuteToTime(rule.end_min === 1440 ? 1439 : rule.end_min)} disabled={disabled || saving} onChange={(event) => {
            const value = timeToMinute(event.target.value, true);
            if (value != null) updateRule(index, { end_min: value });
          }} aria-label={t('deviceSettings.scheduleEnd')} />
          {kind === 'recording' ? (
            <select value={rule.mode ?? 'event'} disabled={disabled || saving} onChange={(event) => {
              const mode = event.target.value as RecordingRuleMode;
              updateRule(index, { mode, detection_types: mode === 'event' ? rule.detection_types : undefined });
            }}>
              <option value="continuous">{t('deviceSettings.recordModeContinuous')}</option>
              <option value="event">{t('deviceSettings.recordModeEvent')}</option>
              <option value="off">{t('deviceSettings.recordModeNone')}</option>
            </select>
          ) : <span />}
          <button type="button" className="btn-ghost" disabled={disabled || saving} onClick={() => updateDay(dayRules.filter((_, i) => i !== index))}>{t('common.delete')}</button>
          {kind === 'recording' && rule.mode === 'event' && (
            <div style={{ gridColumn: '1 / -1', display: 'flex', flexWrap: 'wrap', gap: 'var(--spacing-3)' }}>
              {DETECTION_TYPES.map((type) => (
                <label key={type} style={{ display: 'inline-flex', alignItems: 'center', gap: 'var(--spacing-1)' }}>
                  <input type="checkbox" checked={rule.detection_types?.includes(type) ?? false} disabled={disabled || saving} onChange={(event) => {
                    const current = new Set<DetectionType>(rule.detection_types ?? []);
                    if (event.target.checked) current.add(type); else current.delete(type);
                    updateRule(index, { detection_types: current.size ? [...current] : undefined });
                  }} />
                  {t(`deviceSettings.scheduleDetectionType${type[0].toUpperCase()}${type.slice(1)}`)}
                </label>
              ))}
              <span className="text-secondary">{t('deviceSettings.scheduleDetectionEmptyHint')}</span>
            </div>
          )}
        </div>
      ))}
      <button type="button" className="btn-ghost" style={{ justifySelf: 'start' }} disabled={disabled || saving || dayRules.length >= 8} onClick={addRule}>{t('deviceSettings.scheduleAddRule')}</button>

      <div style={{ display: 'grid', gap: 'var(--spacing-2)' }}>
        <span>{t('deviceSettings.scheduleCopyToDays')}</span>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--spacing-3)' }}>
          {DAY_KEYS.map((key, day) => day !== selectedDay && (
            <label key={key} style={{ display: 'inline-flex', alignItems: 'center', gap: 'var(--spacing-1)' }}>
              <input type="checkbox" checked={copyDays.has(day)} disabled={disabled || saving} onChange={(event) => {
                setCopyDays((current) => { const next = new Set(current); if (event.target.checked) next.add(day); else next.delete(day); return next; });
              }} />
              {t(`deviceSettings.scheduleDay${key[0].toUpperCase()}${key.slice(1)}`)}
            </label>
          ))}
        </div>
        <button type="button" className="btn-ghost" style={{ justifySelf: 'start' }} disabled={disabled || saving || copyDays.size === 0} onClick={applyCopyDays}>{t('deviceSettings.scheduleCopy')}</button>
      </div>

      {isChannelized && usableChannels.length > 1 && (
        <div style={{ display: 'grid', gap: 'var(--spacing-2)' }}>
          <span>{t('deviceSettings.scheduleCopyToCameras')}</span>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--spacing-3)' }}>
            {usableChannels.filter((channel) => channel.ch !== activeChannel).map((channel) => (
              <label key={channel.ch} style={{ display: 'inline-flex', alignItems: 'center', gap: 'var(--spacing-1)' }}>
                <input type="checkbox" checked={copyChannels.has(channel.ch)} disabled={disabled || saving} onChange={(event) => {
                  setCopyChannels((current) => { const next = new Set(current); if (event.target.checked) next.add(channel.ch); else next.delete(channel.ch); return next; });
                }} />
                {channel.name?.trim() || t('deviceSettings.channelNumber', { channel: channel.ch })}
              </label>
            ))}
          </div>
        </div>
      )}

      <button type="button" className="btn-primary" style={{ justifySelf: 'start' }} disabled={disabled || saving} onClick={() => void save()}>
        {saving ? t('common.saving') : t('common.save')}
      </button>
      {notice && <p className="text-secondary" role="status" style={{ margin: 0 }}>{notice}</p>}
      {error && <p className="error-text" role="alert" style={{ margin: 0 }}>{error}</p>}
    </div>
  );
}
